import React from 'react';

export const InnerArchitectureVisual = ({ isVisible = false }) => {
  return (
    <div className="relative w-full aspect-square max-w-lg mx-auto">
      {/* Outer Glow */}
      <div className={`absolute inset-0 rounded-full bg-[#5a7fa8] opacity-0 blur-[80px] transition-opacity duration-1000 ${isVisible ? 'opacity-20' : ''}`} />
      
      {/* Rotating Outer Ring */}
      <svg
        viewBox="0 0 400 400"
        className="absolute inset-0 w-full h-full rotate-slow"
        style={{ animationDuration: '120s' }}
      >
        <defs>
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#5a7fa8" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#c9a87c" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#5a7fa8" stopOpacity="0.3" />
          </linearGradient>
        </defs>
        <circle
          cx="200"
          cy="200"
          r="180"
          fill="none"
          stroke="url(#ringGradient)"
          strokeWidth="0.5"
          strokeDasharray="8 12"
        />
      </svg>

      {/* Static Architecture */}
      <svg viewBox="0 0 400 400" className="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#c9a87c" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#c9a87c" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5a7fa8" stopOpacity="0" />
            <stop offset="50%" stopColor="#5a7fa8" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#5a7fa8" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Background Grid */}
        <g opacity="0.1">
          {[...Array(8)].map((_, i) => (
            <line
              key={`h-${i}`}
              x1="50"
              y1={50 + i * 43}
              x2="350"
              y2={50 + i * 43}
              stroke="#5a7fa8"
              strokeWidth="0.5"
            />
          ))}
          {[...Array(8)].map((_, i) => (
            <line
              key={`v-${i}`}
              x1={50 + i * 43}
              y1="50"
              x2={50 + i * 43}
              y2="350"
              stroke="#5a7fa8"
              strokeWidth="0.5"
            />
          ))}
        </g>

        {/* Concentric Circles */}
        <circle cx="200" cy="200" r="140" fill="none" stroke="#2a3a4d" strokeWidth="1" className={`transition-all duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
        <circle cx="200" cy="200" r="100" fill="none" stroke="#5a7fa8" strokeWidth="0.5" strokeDasharray="4 8" className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-60' : 'opacity-0'}`} />
        <circle cx="200" cy="200" r="60" fill="none" stroke="#c9a87c" strokeWidth="1" className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
        
        {/* Core Glow */}
        <circle cx="200" cy="200" r="40" fill="url(#coreGlow)" className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />
        <circle cx="200" cy="200" r="12" fill="#c9a87c" className={`breathe transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />

        {/* Connection Lines with Animation */}
        <g className={`transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <line x1="200" y1="60" x2="200" y2="140" stroke="url(#lineGradient)" strokeWidth="1" />
          <line x1="200" y1="260" x2="200" y2="340" stroke="url(#lineGradient)" strokeWidth="1" />
          <line x1="60" y1="200" x2="140" y2="200" stroke="url(#lineGradient)" strokeWidth="1" />
          <line x1="260" y1="200" x2="340" y2="200" stroke="url(#lineGradient)" strokeWidth="1" />
        </g>

        {/* Diagonal Lines */}
        <g className={`transition-all duration-1000 delay-900 ${isVisible ? 'opacity-40' : 'opacity-0'}`}>
          <line x1="90" y1="90" x2="155" y2="155" stroke="#5a7fa8" strokeWidth="0.5" />
          <line x1="310" y1="90" x2="245" y2="155" stroke="#5a7fa8" strokeWidth="0.5" />
          <line x1="90" y1="310" x2="155" y2="245" stroke="#5a7fa8" strokeWidth="0.5" />
          <line x1="310" y1="310" x2="245" y2="245" stroke="#5a7fa8" strokeWidth="0.5" />
        </g>
      </svg>

      {/* Labels */}
      <div className={`absolute top-4 left-1/2 -translate-x-1/2 text-center transition-all duration-700 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
        <span className="text-xs tracking-[0.3em] text-[#5a7fa8] uppercase">Perception</span>
      </div>
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-center transition-all duration-700 delay-1100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <span className="text-xs tracking-[0.3em] text-[#5a7fa8] uppercase">Presence</span>
      </div>
      <div className={`absolute left-4 top-1/2 -translate-y-1/2 text-center transition-all duration-700 delay-1200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
        <span className="text-xs tracking-[0.3em] text-[#5a7fa8] uppercase">Pattern</span>
      </div>
      <div className={`absolute right-4 top-1/2 -translate-y-1/2 text-center transition-all duration-700 delay-1300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
        <span className="text-xs tracking-[0.3em] text-[#5a7fa8] uppercase">Decision</span>
      </div>

      {/* Corner Decorative Nodes */}
      <div className={`absolute top-16 left-16 w-2 h-2 rounded-full bg-[#5a7fa8] float-slow transition-opacity duration-1000 delay-500 ${isVisible ? 'opacity-60' : 'opacity-0'}`} />
      <div className={`absolute top-16 right-16 w-2 h-2 rounded-full bg-[#5a7fa8] float-reverse transition-opacity duration-1000 delay-600 ${isVisible ? 'opacity-60' : 'opacity-0'}`} />
      <div className={`absolute bottom-16 left-16 w-2 h-2 rounded-full bg-[#c9a87c] float-reverse transition-opacity duration-1000 delay-700 ${isVisible ? 'opacity-60' : 'opacity-0'}`} />
      <div className={`absolute bottom-16 right-16 w-2 h-2 rounded-full bg-[#c9a87c] float-slow transition-opacity duration-1000 delay-800 ${isVisible ? 'opacity-60' : 'opacity-0'}`} />
    </div>
  );
};

export default InnerArchitectureVisual;
